import { supabase } from './supabase';

// ─── Shared task actions ─────────────────────────────────────────────────────
// One place for the mutations the dashboard fires from many surfaces (Up Next
// queue, timeline blocks, warning rows, NOW card, popover). Every caller is
// expected to refetch (onUpdate) and nudge the scheduler (triggerScheduler)
// afterwards — these helpers only write.

// Manual ordering convention: tasks.sort_order >= 1 is an explicit user rank
// (1 = do first). 0 or null means "auto" — the scheduler decides. The engine
// (scheduler-engine.js rankTasks) and the client mirrors honour the same rule.
export const manualRank = (t) => (t.sort_order >= 1 ? t.sort_order : null);

export async function completeTask(task, newDone) {
  const now = new Date().toISOString();
  await supabase.from('tasks').update({
    done: newDone,
    done_at: newDone ? now : null,
    status: newDone ? 'done' : 'todo',
  }).eq('id', task.id);

  // Mirror TaskRow's behaviour: completing logs a time entry, un-completing
  // removes it, so Time Tracking stays truthful no matter where the tick lands.
  if (newDone && task.duration_minutes) {
    await supabase.from('time_entries').insert({
      task_id: task.id,
      project_id: task.goal_id || null,
      role_id: task.role_id || null,
      date: now.split('T')[0],
      duration_minutes: task.duration_minutes,
      title: task.title,
    });
  }
  if (!newDone) {
    await supabase.from('time_entries').delete().eq('task_id', task.id);
  }
}

// 09:00 local on the target day — the scheduler treats earliest_start as a
// hard "not before", so morning-of is the natural defer anchor.
const at9am = (d) => {
  const x = new Date(d);
  x.setHours(9, 0, 0, 0);
  return x;
};

export function deferTargets() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextMonday = new Date();
  nextMonday.setDate(nextMonday.getDate() + ((8 - nextMonday.getDay()) % 7 || 7));
  return {
    tomorrow: at9am(tomorrow),
    nextweek: at9am(nextMonday),
  };
}

// when: 'tomorrow' | 'nextweek' | Date
export async function deferTask(task, when) {
  const targets = deferTargets();
  const until = when instanceof Date ? at9am(when) : targets[when] || targets.tomorrow;

  const patch = { earliest_start: until.toISOString() };
  // A manual rank means "in today's queue" — deferring leaves the queue.
  if (task.sort_order >= 1) patch.sort_order = 0;
  // The engine rejects earliest_start past the deadline as invalid. Deferring
  // beyond your own deadline is choosing a new one, so move it with the task
  // (same day, 17:00) rather than silently producing an unschedulable task.
  if (task.deadline && new Date(task.deadline) < until) {
    const d = new Date(until);
    d.setHours(17, 0, 0, 0);
    patch.deadline = d.toISOString();
  }
  await supabase.from('tasks').update(patch).eq('id', task.id);
  return until;
}

// Persist an explicit queue order: ranks 1..n in the given order. Fire the
// updates concurrently; callers refetch once after.
export async function applyManualOrder(orderedTasks) {
  await Promise.all(orderedTasks.map((t, i) =>
    supabase.from('tasks').update({ sort_order: i + 1 }).eq('id', t.id)
  ));
}

export async function clearManualOrder(tasks) {
  const ranked = tasks.filter(t => t.sort_order >= 1);
  await Promise.all(ranked.map(t =>
    supabase.from('tasks').update({ sort_order: 0 }).eq('id', t.id)
  ));
}
